import { Utils } from '../Utils';
import requestPromise from 'request-promise-native';
import { amorphicStatic, supertypeClass } from '@haventech/amorphic';

import { UwFlatExtra } from '../../../common/js/interfaces/UwFlatExtra';
import { BenefitType, Product, RiderType } from '../static/Product';
import { ProductApiTypes } from '@hit/policy-admin-types';
import { FlatExtra } from '../customer/FlatExtra';
import { Policy } from '../customer/Policy';
import { CommonUtils } from '../static/CommonUtils';
import { HavenStats } from '../havenStats/HavenStats';
import { serverOnly } from '../helpers';
import { LoggerSingleton, Category, PiiLevelEnum } from '../logger/LoggerSingleton';
import { Application } from '../customer/Application';
import { Rider } from './ProductServiceUtils';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../logger/UILoggerSingleton';

export interface PremiumRequest {
  product: string;
  productType: string;
  state: string;
  gender: '1' | '2';
  ratingCode: string;
  dob: Date;
  effectiveDate: Date;
  term: number;
  face: number;
  waiver: boolean;
  flatExtras: Array<UwFlatExtra>;
  paymentFrequency: string;
  riders?: Array<Rider>;
  asOfDate?: Date;
}
export interface PremiumSolveRequest {
  product: string;
  productType: string;
  state: string;
  gender: '1' | '2';
  ratingCode: string;
  dob: Date;
  effectiveDate: Date;
  term: number;
  face?: number;
  waiver: boolean;
  flatExtras: Array<UwFlatExtra>;
  paymentFrequency: string;
  riders?: Array<Rider>;
  asOfDate?: Date;
  premium: number;
}

export { Rider } from './ProductServiceUtils';

export interface PremiumRequestList {
  requests: Array<PremiumRequest>;
}

export interface PremiumResponse {
  version: string;
  base: number;
  waiver: number;
  riders?: Array<Rider>;
  flatExtras?: Array<FlatExtraResponse>;
  annualBaseRates?: Array<number>;
  annualWaiverRates?: Array<number>;
  totalTargetPremium: number;
  annualBase: number;
}

export interface ProductSpecRequest {
  insuredAge: number;
  product: string;
  productType: string;
  duration: number;
  state: string;
  referenceId?: string;
  asOfDate?: Date;
}

export interface ProductSpecResponse {
  convertibleAllow: string;
  convertibleEndAge: number;
  convertibleMinYears: number;
  convertibleMaxYears: number;
  fixedLoanInterestRate?: number;
  variableLoanInterestRate?: number;
}

export interface PremiumResponseList {
  responses: Array<PremiumResponse>;
}

export interface FlatExtraResponse {
  name: string;
  premium: number;
  ruleCode?: string;
  amount?: number;
  expiryAfterInYears?: number;
}

enum PaymentFrequency {
  Annual = 'Annual',
  Monthly = 'Monthly',
}

export interface PayloadSegmentInfo {
  asOfDate: Date;
  rateClass: string;
  amount: number;
  riders?: Array<Rider>;
  hasWaiver?: boolean;
  flatExtras?;
  includeAdbr?: boolean;
}
export interface SequenceQueryRequest {
  query: string;
  variables: ProductApiTypes.QuerysequenceArgs;
}
export interface SequenceOptions {
  productCode: string;
  referenceId: string;
}

@supertypeClass({ toClient: false, toServer: true })
export class ProductServiceClient {
  static readonly PRODUCT_SERVICE_ENDPOINT: string = 'endpointProduct';
  static readonly PRODUCT_SERVICE_JWT_TOKEN: string = 'cpApiGatewayJWTToken';
  static readonly productSpecQuery: string = `query productSpec($request: ProductSpecificationsRequest) {
          productSpec(request: $request) {
            payload {
              convertibleAllow
              convertibleEndAge
              convertibleMinYears
              convertibleMaxYears
              fixedLoanInterestRate
              variableLoanInterestRate
            }
          }
        }`;

  //fullAnnalBasePremium & fullAnnualBaseCurrentPremiums only for RTR,
  //may not want to request it every time
  static readonly premiumQuery: string = `query calc_premiums($request: PremiumRequest) {
          premiums(request: $request) {
            payload {
              policies {
                version
                totalTargetPremium
                segments {
                  riderType
                  benefits {
                    code
                    basePremium
                    annualBasePremium
                    annualFee
                    annualTableRatingPremium
                    fullBasePremiums
                    fee
                    tableRatingPremium
                    flatExtras {
                      name
                      premium
                    }
                  }
                }
              }
            }
          }
        }`;
  static readonly sequenceQuery: string = `query sequence($request: SequenceRequest) {
    sequence(request: $request) {
      payload {
        productCode
        sequenceName
        sequenceNumber
      }
    }
  }`;

  static getProductAPIHeaders() {
    const cpApiGatewayJWTToken = amorphicStatic.config.nconf.get(ProductServiceClient.PRODUCT_SERVICE_JWT_TOKEN);
    const globalHeaders = LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.();
    return {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + cpApiGatewayJWTToken,
      ...globalHeaders,
    };
  }

  public static getGlobalHeadersForContext() {
    const globalHeaders = LoggerSingleton.getInstance()?.getContextFromLocalStorage?.();

    return {
      globalId: globalHeaders?.globalId,
      globalSource: globalHeaders?.globalSource,
    };
  }

  private static normalizePaymentFrequency(paymentFrequency): PaymentFrequency {
    const annualRegex = /annual/i;
    const monthlyRegex = /monthly/i;

    if (annualRegex.test(paymentFrequency)) {
      return PaymentFrequency.Annual;
    } else if (monthlyRegex.test(paymentFrequency)) {
      return PaymentFrequency.Monthly;
    } else {
      LoggerSingleton.getInstance().warn(
        {
          module: 'common:ProductServiceClient',
          function: 'normalizePaymentFrequency',
          category: Category.request,

          data: {
            paymentFrequency: paymentFrequency,
          },
        },
        'Using the default Monthly payment frequency for product-service call'
      );
      return PaymentFrequency.Monthly;
    }
  }

  static buildPremiumRequest(policyNumber: string, version: string, options: PremiumRequest) {
    const product: string = options.product;
    const productType: string = options.productType;

    const policyPremiumRequestObj = this.buildPolicyPremiumRequest(version, options);
    const requestObj = {
      query: ProductServiceClient.premiumQuery,
      variables: {
        request: {
          context: {
            requestId: Utils.generateUuid(),
            userId: Product.products[product].channel.code,
            referenceId: policyNumber,
            referenceType: 'PolicyNumber',
            eventName: '',
            ...ProductServiceClient.getGlobalHeadersForContext(),
          },
          payload: {
            policies: [policyPremiumRequestObj],
          },
        },
      },
    };

    // Debugger for the ticket HL-42869
    if (productType === null) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'ProductServiceClient',
          function: 'buildPremiumRequest',
          category: UICategory.request,

          data: {
            productType: policyPremiumRequestObj,
          },
        },
        'HL-42869: ProductType null issue'
      );
    }
    return requestObj;
  }

  static buildProductSpecRequest(options: ProductSpecRequest) {
    const requestObj = {
      query: ProductServiceClient.productSpecQuery,
      variables: {
        request: {
          context: {
            requestId: Utils.generateUuid(),
            userId: Product.products[options.product].channel.code,
            referenceId: options.referenceId,
            referenceType: 'application_id',
            eventName: '',
            ...ProductServiceClient.getGlobalHeadersForContext(),
          },
          payload: {
            insuredAge: options.insuredAge,
            product: {
              type: options.productType,
              code: options.product,
              state: options.state,
            },
            duration: options.duration,
            asOfDate: options.asOfDate,
          },
        },
      },
    };

    return requestObj;
  }

  static async publicFetchProductSpec(requestOptions: ProductSpecRequest): Promise<ProductSpecResponse> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'controller.publicFetchProductSpec',
        function: 'publicFetchProductSpec',
        category: UICategory.request,
        data: {
          productServicePayload: requestOptions,
        },
      },
      'fetching a product spec from the product service'
    );
    return ProductServiceClient.getProductSpec(requestOptions);
  }

  @serverOnly()
  static buildSequenceRequest(
    payload: ProductApiTypes.SequenceReq,
    context: ProductApiTypes.ContextReq
  ): SequenceQueryRequest {
    const requestObj = {
      query: ProductServiceClient.sequenceQuery,
      variables: {
        request: {
          context,
          payload,
        },
      },
    };
    return requestObj;
  }

  /***
   *
   * @param asOfDate
   * @param rateClass
   * @param amount - face amount of base policy
   * @param riderType - null if base policy, else GIR or RTR
   * @param waiver - boolean, if waiver selected. This benefit is applied on all segments
   * @param flatExtra - flat extras { name: string, rate: number }, this is applied on all segments
   * @param adbr - boolean; illustration requests currently includes adbr benefit for base policy, with units =0
   *
   * generates payload segments details for product service calls (premiums, vst, paymentoptions, illustration calls)
   *
   */
  static generateProductServicePayloadSegments(segmentInfo: PayloadSegmentInfo, isPremiumSolve?: boolean) {
    let id = 1;
    const segments = [];
    //base policy segment
    const segment = this.generateSegment(
      id + '',
      segmentInfo.asOfDate,
      segmentInfo.rateClass,
      segmentInfo.amount,
      null,
      segmentInfo.hasWaiver,
      segmentInfo.flatExtras,
      segmentInfo.includeAdbr,
      null,
      null,
      null,
      null,
      null,
      isPremiumSolve
    );
    segments.push(segment);

    if (segmentInfo.riders) {
      //if riders are included, generate payload segment for each rider
      for (const rider of segmentInfo.riders) {
        id++;
        const riderSegment = this.generateSegment(
          id + '',
          segmentInfo.asOfDate,
          segmentInfo.rateClass,
          rider.amount,
          rider.type,
          segmentInfo.hasWaiver,
          segmentInfo.flatExtras,
          false,
          rider.plannedYears,
          rider.paymentFrequency,
          rider.accelerationPercent,
          rider.monthlyIncreaseOption,
          rider.puaAddedToBenefitPool,
          false // for riders, pass false because we don't want to override the behavior
        );
        segments.push(riderSegment);
      }
    }
    return segments;
  }

  /**
   * generate segment/benefit fields and values based on type (Death, GIR, RTR, etc)
   *
   * If flat extras or waiver are provided, include in benefits for all segments (death, gir, or rtr)
   *
   * This function is also called in ProductServiceClient for VST and in IllustrationService for illustrations
   *
   */
  @serverOnly()
  public static generateSegment(
    id: string,
    asOfDate,
    rateClass,
    amount: number,
    riderType?: string,
    waiver?,
    flatExtras?,
    adbr?,
    plannedYears?: number,
    paymentFrequency?: string,
    accelerationPercent?: number,
    monthlyIncreaseOption?: boolean,
    puaAcceleration?: boolean,
    isPremiumSolve?: boolean
  ): ProductApiTypes.Segment {
    let segment;

    //generate segment
    if (RiderType.GIR === riderType || RiderType.RTR === riderType) {
      //GIR or RTR
      segment = {
        id: id,
        riderType: riderType,
        startDate: asOfDate,
        benefits: [], //benefits are populated below
        rating: {
          rateClass: rateClass,
        },
        segmentType: RiderType.GIR === riderType ? 'GIRSegment' : 'RTRSegment',
      };
    } else if (riderType === RiderType.ALIR) {
      segment = {
        id,
        riderType,
        startDate: asOfDate,
        alirPremium: amount,
        benefits: [], //benefits are populated below
        plannedYears,
        paymentFrequency: paymentFrequency ? Utils.capitalizeFirstLetter(paymentFrequency) : paymentFrequency,
      };
    } else if (riderType === RiderType.CIR) {
      segment = {
        id,
        riderType,
        startDate: asOfDate,
        rating: {
          rateClass: rateClass,
        },
        accelerationPercent: accelerationPercent,
        monthlyIncreaseOption: monthlyIncreaseOption,
        puaAcceleration: puaAcceleration,
        benefits: [], //benefits are populated below
      };
    } else {
      //base policy segment
      segment = {
        id: id,
        startDate: asOfDate,
        benefits: [], //benefits populated before
        rating: {
          rateClass: rateClass,
        },
      };
    }

    let riderCode = BenefitType.Death;
    if (riderType === BenefitType.GIR) {
      riderCode = BenefitType.GIR;
    } else if (riderType === BenefitType.CIR) {
      riderCode = BenefitType.CIR;
    }

    //generate benefits for segment
    segment.benefits.push({
      code: riderCode,
      units: amount,
      flatExtras: flatExtras,
    });
    if (waiver) {
      segment.benefits.push({
        code: ProductApiTypes.BenefitCode.Waiver,
        units: !!isPremiumSolve ? 1 : amount,
      });
    }
    if (adbr && !riderType) {
      //Calls by ProductServiceClient for VST and IllustrationService currently include ADBR benefit for base policy segment with value zero.
      //This was brought over to consolidate this function, but doesn't seem to have affect on results.
      segment.benefits.push({
        code: ProductApiTypes.BenefitCode.Adbr,
        units: 0,
      });
    }
    return segment;
  }

  private static buildPolicyPremiumRequest(version: string, options: PremiumRequest) {
    const dob: Date = options.dob;
    const effectiveDate: Date = options.effectiveDate;
    const duration: number = options.term;
    const gender: string = options.gender === '1' ? 'M' : 'F';
    const product: string = options.product;
    const productType: string = options.productType;
    const state: string = options.state;
    const hasWaiver: boolean = options.waiver ? true : false;
    let rateClass: string = options.ratingCode.toString();
    // workaround for LifeBridge, which always expects a rateClass of ‘2’
    if (product === Product.products.ET10.code) {
      rateClass = '2';
    }
    const paymentFrequency: string = ProductServiceClient.normalizePaymentFrequency(options.paymentFrequency);
    const flatExtras = options.flatExtras.map((fe) => {
      return { name: fe.ruleCode, rate: fe.amount };
    });
    const units: number = options.face;

    const segmentInfo: PayloadSegmentInfo = {
      asOfDate: effectiveDate,
      rateClass: rateClass,
      amount: units,
      riders: options.riders,
      hasWaiver: hasWaiver,
      flatExtras: flatExtras,
      includeAdbr: false,
    };
    const segments = this.generateProductServicePayloadSegments(segmentInfo);
    return {
      version: version,
      asOfDate: options.asOfDate || Utils.now(),
      startDate: effectiveDate,
      duration: duration,
      totalUnits: units,
      paymentFrequency: paymentFrequency,
      product: {
        type: productType,
        code: product,
        state: state,
      },
      insured: {
        dob: dob,
        gender: gender,
      },
      segments: segments,
    };
  }

  private static getPolicies(version: string, options: PremiumRequestList) {
    const policiesArray = [];

    options.requests.forEach((premiumRequest) => {
      const policyPremiumRequestObj = this.buildPolicyPremiumRequest(version, premiumRequest);
      policiesArray.push(policyPremiumRequestObj);

      const productType: string = premiumRequest.productType;
      // Debugger for the ticket HL-42869
      if (productType === null) {
        UILoggerSingleton.getInstance().info(
          {
            module: 'ProductServiceClient',
            function: 'buildPremiumRequest',
            category: UICategory.request,
            data: {
              productType: policyPremiumRequestObj,
            },
          },
          'HL-42869: ProductType null issue'
        );
      }
    });

    return policiesArray;
  }

  public static buildPremiumRequestList(policyNumber: string, version: string, options: PremiumRequestList) {
    const requestObj = {
      query: ProductServiceClient.premiumQuery,
      variables: {
        request: {
          context: {
            requestId: Utils.generateUuid(),
            userId: Product.products[options.requests[0].product].channel.code,
            referenceId: policyNumber,
            referenceType: 'PolicyNumber',
            eventName: '',
            ...ProductServiceClient.getGlobalHeadersForContext(),
          },
          payload: {
            policies: this.getPolicies(version, options),
          },
        },
      },
    };
    return requestObj;
  }

  static buildProductSpecResponse(response, applicationID: string): ProductSpecResponse {
    if (response.errors) {
      UILoggerSingleton.getInstance().error({
        module: 'ProductServiceClient',
        function: 'buildProductSpecResponse',
        category: UICategory.request,
        context: { piiLevel: UIPiiLevelEnum.s2 },
        error: {
          isHumanRelated: false,
          data: {
            response,
            applicationId: applicationID,
          },
        },
      });
      throw new Error(`Could not get product specs for application ${applicationID}`);
    }

    const payload = response.data.productSpec.payload;
    const productSpecResponse: ProductSpecResponse = {
      convertibleAllow: payload.convertibleAllow,
      convertibleEndAge: payload.convertibleEndAge,
      convertibleMinYears: payload.convertibleMinYears,
      convertibleMaxYears: payload.convertibleMaxYears,
      fixedLoanInterestRate: payload.fixedLoanInterestRate,
      variableLoanInterestRate: payload.variableLoanInterestRate,
    };
    return productSpecResponse;
  }

  @serverOnly()
  static buildSequenceResponse(response, product: string, applicationNumber: string): ProductApiTypes.SequenceResponse {
    if (response.errors) {
      LoggerSingleton.getInstance().error({
        module: 'ProductServiceClient',
        function: 'buildSequenceResponse',
        category: Category.request,
        context: { piiLevel: PiiLevelEnum.s2 },
        error: {
          isHumanRelated: false,
          data: {
            response,
            applicationNumber,
            product,
          },
        },
      });
      HavenStats.singletonStatsDClient().increment('ProductApi.buildSequenceResponse.request.error.count');
      throw new Error(`Product responded with errors while fetching next sequence for ${product}`);
    }
    const sequenceResponse = {
      productCode: response.data.sequence.payload.productCode,
      sequenceName: response.data.sequence.payload.sequenceName,
      sequenceNumber: response.data.sequence.payload.sequenceNumber,
    };
    return { payload: sequenceResponse };
  }

  private static buildPremiumResponse(policyResponseData): PremiumResponse {
    const premiumResponse: PremiumResponse = {
      version: '0',
      base: 0,
      waiver: 0,
      riders: [],
      flatExtras: [],
      annualBaseRates: [],
      annualWaiverRates: [],
      totalTargetPremium: 0,
      annualBase: 0,
    };
    premiumResponse.version = policyResponseData.version;
    premiumResponse.totalTargetPremium = policyResponseData.totalTargetPremium;

    policyResponseData.segments.forEach((segment) => {
      const benefits = segment.benefits;
      const riderType = segment.riderType;
      let premium = 0;
      let waiver = 0;
      let flatExtras = [];
      let annualBaseRates = [];
      let annualWaiverRates = [];
      let annualBase = 0;

      benefits.forEach((benefit) => {
        switch (benefit.code) {
          case BenefitType.Death: {
            annualBase = benefit.annualBasePremium + benefit.annualFee + benefit.annualTableRatingPremium;
            premium = benefit.basePremium + benefit.fee + benefit.tableRatingPremium;
            annualBaseRates = benefit.fullBasePremiums.map(
              (premium) => premium + benefit.fee + benefit.tableRatingPremium
            );
            flatExtras = benefit.flatExtras;
            if (!riderType && flatExtras && flatExtras.length) {
              premiumResponse.flatExtras = flatExtras.map((flatExtra) => {
                return {
                  name: flatExtra.name,
                  premium: flatExtra.premium,
                  ruleCode: flatExtra.ruleCode,
                  amount: flatExtra.amount,
                  expiryAfterInYears: flatExtra.expiryAfterInYears,
                };
              });
            }
            break;
          }
          case BenefitType.GIR: {
            premium = benefit.basePremium + benefit.fee + benefit.tableRatingPremium;
            break;
          }
          case BenefitType.CIR: {
            premium = benefit.basePremium;
            break;
          }
          case BenefitType.Waiver: {
            waiver = benefit.basePremium;
            if (benefit.fullBasePremiums) {
              annualWaiverRates = benefit.fullBasePremiums.map(
                (premium) => premium + benefit.fee + benefit.tableRatingPremium
              );
            }
            break;
          }
        }
      });
      if (riderType) {
        //gir or rtr segment
        const rider: Rider = {
          type: riderType,
          amount: 0,
          premium,
          waiver,
          flatExtras,
        };
        premiumResponse.riders.push(rider);
      } else {
        //non rider
        premiumResponse.base = premium;
        premiumResponse.waiver = waiver;
        premiumResponse.annualBase = annualBase;
        premiumResponse.annualBaseRates = annualBaseRates;
        premiumResponse.annualWaiverRates = annualWaiverRates;
      }
    });
    return premiumResponse;
  }

  private static buildResponse(response, policyNumber: string): PremiumResponse {
    if (response.errors) {
      UILoggerSingleton.getInstance().error({
        module: 'ProductServiceClient',
        function: 'buildResponse',
        category: UICategory.request,
        context: { piiLevel: UIPiiLevelEnum.s2 },
        error: {
          isHumanRelated: false,
          data: {
            response,
            policyNumber: policyNumber,
          },
        },
      });
      throw new Error(`Could not get a premium for policy ${policyNumber}`);
    }

    const policies = response.data.premiums.payload.policies;
    const policyResponseData = policies[0];
    return this.buildPremiumResponse(policyResponseData);
  }

  @serverOnly()
  static async getProductSpec(options: ProductSpecRequest): Promise<ProductSpecResponse> {
    const requestOptions = {
      method: 'POST',
      uri: `${amorphicStatic.config.nconf.get(ProductServiceClient.PRODUCT_SERVICE_ENDPOINT)}/gql`,
      body: ProductServiceClient.buildProductSpecRequest(options),
      json: true,
      headers: this.getProductAPIHeaders(),
    };
    return requestPromise(requestOptions).then((response) =>
      ProductServiceClient.buildProductSpecResponse(response, options.referenceId)
    );
  }

  @serverOnly()
  static getPremium(policyNumber: string, version: string, options: PremiumRequest): Promise<PremiumResponse> {
    const requestOptions = {
      method: 'POST',
      uri: `${amorphicStatic.config.nconf.get(ProductServiceClient.PRODUCT_SERVICE_ENDPOINT)}/gql`,
      body: ProductServiceClient.buildPremiumRequest(policyNumber, version, options),
      json: true,
      headers: this.getProductAPIHeaders(),
    };

    return requestPromise(requestOptions).then((response) =>
      ProductServiceClient.buildResponse(response, policyNumber)
    );
  }

  @serverOnly()
  static async getSequenceReponse(payload, context, requestTimeOut) {
    const requestOptions = {
      method: 'POST',
      uri: `${amorphicStatic.config.nconf.get(ProductServiceClient.PRODUCT_SERVICE_ENDPOINT)}/gql`,
      body: ProductServiceClient.buildSequenceRequest(payload, context),
      json: true,
      timeout: requestTimeOut,
      headers: this.getProductAPIHeaders(),
    };

    return requestPromise(requestOptions);
  }

  @serverOnly()
  static async getSequence(options: SequenceOptions): Promise<string> {
    const delay = (ms) => new Promise((res) => setTimeout(res, ms));

    const maxAttempts = amorphicStatic.config.nconf.get('sequenceRequestEndpointParams:MaxAttempts') || 2;
    const requestTimeOut = amorphicStatic.config.nconf.get('sequenceRequestEndpointParams:RequestTimeOut') || 10000;
    const retryDelay = amorphicStatic.config.nconf.get('sequenceRequestEndpointParams:RetryDelay') || 2000;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const requestTime = process.hrtime();
        HavenStats.singletonStatsDClient().increment('ProductServiceClient.getSequence.request.count');

        const payload = {
          sequenceName: ProductApiTypes.SequenceName.PolicyNumber,
          productCode: options && ProductApiTypes.ProductCode[options.productCode],
        };
        const context = {
          requestId: Utils.generateUuid(),
          userId: options && Product.products[options.productCode].channel.code,
          referenceId: options && options.referenceId,
          referenceType: 'ApplicationNumber',
          ...ProductServiceClient.getGlobalHeadersForContext(),
        };

        const response = await this.getSequenceReponse(payload, context, requestTimeOut);

        const timeDiffInMilliseconds = Utils.getTimeOffset(requestTime);
        HavenStats.singletonStatsDClient().timing(
          'ProductServiceClient.getSequence.request.time',
          timeDiffInMilliseconds,
          {
            unit: 'milliseconds',
          }
        );

        const sequenceResponse = ProductServiceClient.buildSequenceResponse(
          response,
          payload.productCode,
          context.referenceId
        );

        return sequenceResponse && String(sequenceResponse.payload.sequenceNumber);
      } catch (e) {
        // TODO: HL-37483 - Since Graphql doesn't have error status codes, spitting out error messages
        // and doing one retry for all failed attepmts. We could collect some logs and selectively implement
        // a more refined retry.
        const retry = attempt < maxAttempts;
        const err = {
          name: e?.name,
          message: e?.message,
          stack: e?.stack,
          data: {
            applicationNumber: options && options.referenceId,
          },
        };
        LoggerSingleton.getInstance().error(
          {
            module: 'ProductServiceClient',
            function: 'getSequence',
            category: Category.request,
            error: err,
          },
          `${retry ? 'Retrying: ' : ''}Unable to fetch next sequence for ${
            options && options.productCode
          } after ${attempt} attempt(s)`
        );

        if (retry) {
          HavenStats.singletonStatsDClient().increment('ProductServiceClient.getSequence.request.retry.count');
          await delay(retryDelay);
        } else {
          HavenStats.singletonStatsDClient().increment('ProductServiceClient.getSequence.request.error.count');
          throw new Error(
            `Unable to get next sequence for Product ${options && options.productCode} and referenceId ${
              options && options.referenceId
            } after ${maxAttempts} attempts`
          );
        }
      }
    }
  }

  private static buildResponseList(response, policyNumber: string): PremiumResponseList {
    if (response.errors) {
      UILoggerSingleton.getInstance().error({
        module: 'ProductServiceClient',
        function: 'buildResponseList',
        category: UICategory.request,
        context: { piiLevel: UIPiiLevelEnum.s2 },
        error: {
          isHumanRelated: false,
          data: {
            response,
            policyNumber,
          },
        },
      });
      throw new Error(`Could not get a premium for policy ${policyNumber}`);
    }

    const premiumResponseList: PremiumResponseList = {
      responses: [],
    };

    const policiesResponseData = response.data.premiums.payload.policies;

    policiesResponseData.forEach((policyResponseData) => {
      const premiumResponse = this.buildPremiumResponse(policyResponseData);
      premiumResponseList.responses.push(premiumResponse);
    });

    return premiumResponseList;
  }

  @serverOnly()
  static getMultiplePremiums(
    policyNumber: string,
    version: string,
    options: PremiumRequestList
  ): Promise<PremiumResponseList> {
    const requestOptions = {
      method: 'POST',
      uri: `${amorphicStatic.config.nconf.get(ProductServiceClient.PRODUCT_SERVICE_ENDPOINT)}/gql`,
      body: ProductServiceClient.buildPremiumRequestList(policyNumber, version, options),
      json: true,
      headers: this.getProductAPIHeaders(),
    };

    return requestPromise(requestOptions).then((response) =>
      ProductServiceClient.buildResponseList(response, policyNumber)
    );
  }

  // todo: HL-74936 - will fix the way quote objects are cloned during cancel/reapply, so that a canceled application will have a quote and quote.healthClass
  @serverOnly()
  public static async fetchHealthClassFromApplicationQuote(policy: Policy): Promise<string> {
    if (policy.application.quote?.healthClass) {
      return policy.application.quote.healthClass;
    } else if (policy.wasCanceled()) {
      const reappliedApplications: Application[] = await Application.persistorFetchByQuery({
        originalApplicationNumber: policy.application.originalApplicationNumber || policy.application.applicationNumber,
        type: 'active',
      });
      if (reappliedApplications[0]?.quote?.healthClass) {
        return reappliedApplications[0].quote.healthClass;
      }
    }
  }
}
